<template>
  <div
    v-if="visible"
    class="nup-picker"
    @click.self="$emit('close')"
  >
    <div class="nup-picker__wrap">
      <div
        class="nup-picker__stage"
        :class="isAdvanced ? 'is-advanced' : ''"
      >
        <img
          class="nup-picker__base"
          :src="dialogSrc"
          alt=""
          width="750"
        >
        <button
          v-for="item in cells"
          :key="item.key"
          type="button"
          class="nup-picker__cell"
          :class="'is-' + item.key"
          @click="pick(item.key)"
        >
          <template v-if="selectedKey === item.key">
            <img
              class="nup-picker__glow"
              :src="glowSrc"
              alt=""
            >
            <img
              class="nup-picker__check"
              :src="checkSrc"
              alt=""
            >
          </template>
        </button>
        <div class="nup-picker__cta">
          <button
            type="button"
            class="nup-picker__pick"
            :disabled="disabled"
            :style="isPC ? null : { backgroundImage: 'url(' + btnBg + ')' }"
            @click="$emit('select', selectedKey)"
          >
            {{ copy.chooseThis }}
          </button>
        </div>
      </div>
      <button
        type="button"
        class="nup-picker__close"
        @click="$emit('close')"
      >
        <img
          :src="closeIcon"
          :alt="copy.close"
          width="48"
          height="48"
        >
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ref, watch } from 'vue-demi';
import { DEFAULT_CHOICE_KEY, isChoiceKey, type ChoiceKey } from '@ark/nup-shared';
import { getCopy } from '../constants/copy';
import { useNupLang } from '../composables/use-nup-lang';
import { usePageTypeFlag } from '../composables/use-page-type';
import btnBg from '../assets/rewards/btn-bg.png';
import closeIcon from '../assets/dialog-close.svg';
import entryDialogSrc from '../assets/rewards/choice-dialog.png';
import advancedDialogSrc from '../assets/rewards/choice-dialog-advanced.png';
import enEntryDialogSrc from '../assets/en/choice-dialog.png';
import enAdvancedDialogSrc from '../assets/en/choice-dialog-advanced.png';
import glowSrc from '../assets/rewards/choice-dialog-glow.png';
import checkSrc from '../assets/rewards/choice-dialog-check.png';
interface ChoiceCell {
  key: ChoiceKey;
}
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    type: Boolean,
    default: false
  },
  choiceKey: {
    type: String,
    default: DEFAULT_CHOICE_KEY
  },
  group: {
    type: String,
    default: 'entry'
  },
  disabled: {
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const ENTRY_CELLS: readonly ChoiceCell[] = [{
    key: 'nvidia'
  }, {
    key: 'btc'
  }, {
    key: 'voucher'
  }, {
    key: 'hsbc'
  }];
  const ADVANCED_CELLS: readonly ChoiceCell[] = ENTRY_CELLS.filter(item => item.key !== 'voucher');
  const props = __props;
  const {
    isEn
  } = useNupLang();
  const {
    isPC
  } = usePageTypeFlag();
  const copy = computed(() => getCopy(isEn.value));
  const selectedKey = ref<ChoiceKey>(DEFAULT_CHOICE_KEY);
  const isAdvanced = computed(() => props.group === 'advanced');
  const dialogSrc = computed(() => {
    if (isAdvanced.value) {
      return isEn.value ? enAdvancedDialogSrc : advancedDialogSrc;
    }
    return isEn.value ? enEntryDialogSrc : entryDialogSrc;
  });
  const cells = computed(() => {
    return isAdvanced.value ? ADVANCED_CELLS : ENTRY_CELLS;
  });
  function pick(key: ChoiceKey): void {
    selectedKey.value = key;
  }
  function syncSelected(): void {
    const next = isChoiceKey(props.choiceKey) ? props.choiceKey : DEFAULT_CHOICE_KEY;
    if (props.group === 'advanced' && next === 'voucher') {
      selectedKey.value = DEFAULT_CHOICE_KEY;
      return;
    }
    selectedKey.value = next;
  }
  watch(() => props.visible, visible => {
    if (visible) {
      syncSelected();
    }
  });
  return {
    btnBg,
    closeIcon,
    glowSrc,
    checkSrc,
    isPC,
    copy,
    selectedKey,
    isAdvanced,
    dialogSrc,
    cells,
    pick
  };
};
export default __sfc_main;
</script>

<style scoped>
.nup-picker {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.7);
}

.nup-picker__wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 750px;
}

.nup-picker__stage {
  position: relative;
  width: 750px;
}

.nup-picker__base {
  display: block;
  width: 750px;
  height: auto;
  pointer-events: none;
}

.nup-picker__cell {
  position: absolute;
  z-index: 2;
  width: 285px;
  height: 134px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

/* 叠框 285×134，对齐底图四张 259×110 卡（外扩 13 / 12） */
.nup-picker__cell.is-nvidia {
  left: 97px;
  top: 242px;
}

.nup-picker__cell.is-btc {
  left: 368px;
  top: 242px;
}

.nup-picker__cell.is-voucher {
  left: 97px;
  top: 362px;
}

.nup-picker__cell.is-hsbc {
  left: 368px;
  top: 362px;
}

/* 8 万三选一：汇丰左上、比特币右上、英伟达底部居中 */
.nup-picker__stage.is-advanced .nup-picker__cell.is-hsbc {
  left: 98px;
  top: 242px;
}

.nup-picker__stage.is-advanced .nup-picker__cell.is-btc {
  left: 368px;
  top: 242px;
}

.nup-picker__stage.is-advanced .nup-picker__cell.is-nvidia {
  left: 232px;
  top: 362px;
}

.nup-picker__glow {
  display: block;
  width: 285px;
  height: 134px;
  pointer-events: none;
}

.nup-picker__check {
  position: absolute;
  top: 26px;
  right: 26px;
  width: 32px;
  height: 32px;
  pointer-events: none;
}

.nup-picker__stage:not(.is-advanced) .nup-picker__cell.is-btc .nup-picker__check,
.nup-picker__stage:not(.is-advanced) .nup-picker__cell.is-hsbc .nup-picker__check,
.nup-picker__stage.is-advanced .nup-picker__cell.is-hsbc .nup-picker__check,
.nup-picker__stage.is-advanced .nup-picker__cell.is-btc .nup-picker__check {
  right: 0.32rem;
}

.nup-picker__cta {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 508px;
  width: 500px;
  transform: translateX(-50%);
}

.nup-picker__pick {
  display: block;
  width: 500px;
  height: 88px;
  padding: 0;
  border: none;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  color: #fff;
  font-size: 32px;
  font-weight: 600;
  line-height: 88px;
  text-align: center;
  cursor: pointer;
}

.nup-picker__pick:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.nup-picker__close {
  position: relative;
  z-index: 5;
  margin-top: -100px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  width: 48px;
  height: 48px;
}

.nup-picker__close img {
  display: block;
  width: 48px;
  height: 48px;
}

.nup-page.is-pc .nup-picker__wrap,
.nup-page.is-pc .nup-picker__stage,
.nup-page.is-pc .nup-picker__base {
  width: 580px;
}

.nup-page.is-pc .nup-picker__base {
  height: auto;
}

.nup-page.is-pc .nup-picker__cell {
  width: 38%;
  height: 17.16%;
}

.nup-page.is-pc .nup-picker__cell.is-nvidia {
  left: 12.93%;
  top: 30.99%;
}

.nup-page.is-pc .nup-picker__cell.is-btc {
  left: 49.07%;
  top: 30.99%;
}

.nup-page.is-pc .nup-picker__cell.is-voucher {
  left: 12.93%;
  top: 46.35%;
}

.nup-page.is-pc .nup-picker__cell.is-hsbc {
  left: 49.07%;
  top: 46.35%;
}

.nup-page.is-pc .nup-picker__stage.is-advanced .nup-picker__cell.is-hsbc {
  left: 13.07%;
  top: 30.99%;
}

.nup-page.is-pc .nup-picker__stage.is-advanced .nup-picker__cell.is-btc {
  left: 49.07%;
  top: 30.99%;
}

.nup-page.is-pc .nup-picker__stage.is-advanced .nup-picker__cell.is-nvidia {
  left: 30.93%;
  top: 46.35%;
}

.nup-page.is-pc .nup-picker__glow {
  width: 100%;
  height: 100%;
}

.nup-page.is-pc .nup-picker__check {
  top: 19.4%;
  right: 0.22rem;
  width: 11.23%;
  height: 23.88%;
}

.nup-page.is-pc .nup-picker__stage:not(.is-advanced) .nup-picker__cell.is-btc .nup-picker__check,
.nup-page.is-pc .nup-picker__stage:not(.is-advanced) .nup-picker__cell.is-hsbc .nup-picker__check,
.nup-page.is-pc .nup-picker__stage.is-advanced .nup-picker__cell.is-hsbc .nup-picker__check,
.nup-page.is-pc .nup-picker__stage.is-advanced .nup-picker__cell.is-btc .nup-picker__check {
  right: 0.26rem;
}

.nup-page.is-pc .nup-picker__cta {
  top: 65.04%;
  width: 66.67%;
}

.nup-page.is-pc .nup-picker__pick {
  width: 100%;
  height: 48px;
  background: #ff6900;
  background-image: none;
  border-radius: 0.48rem;
  font-size: 16px;
  line-height: 48px;
}

.nup-page.is-pc .nup-picker__close {
  margin-top: -0.6rem;
}
</style>
